import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Loader2, Newspaper, Plus, TrendingUp, Users, Eye, Heart,
  MessageCircle, BarChart3, RefreshCw, Calendar, Shield
} from "lucide-react";
import { CreatePostForm, PostCard } from "../components/SocialFeed";
import ModerationPanel from "../components/ModerationPanel";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow, format, parseISO } from "date-fns";

const EmbeddedLayout = ({ children }) => <>{children}</>;

const AdminSocial = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [pinnedPosts, setPinnedPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState("posts");
  const [organization, setOrganization] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (user?.org_id) {
      fetchOrganization();
      fetchPosts();
      fetchAnalytics();
    }
  }, [user?.org_id]);

  const fetchOrganization = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/organizations/${user.org_id}`, { headers });
      setOrganization(res.data);
    } catch (error) {
      console.error("Failed to fetch org:", error);
    }
  };

  const fetchPosts = async (loadMore = false) => {
    if (!loadMore) setLoading(true);
    if (!loadMore) setLoadError(false);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API}/social/orgs/${user.org_id}/posts?page=${loadMore ? page + 1 : 1}&page_size=10`,
        { headers }
      );
      
      if (loadMore) {
        setPosts([...posts, ...(res.data.posts || [])]);
        setPage(page + 1);
      } else {
        setPinnedPosts(res.data.pinned_posts || []);
        setPosts(res.data.posts || []);
        setPage(1);
      }
      
      setHasMore(res.data.page < res.data.pages);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      if (!loadMore) setLoadError(true);
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API}/social/orgs/${user.org_id}/analytics?days=30`,
        { headers }
      );
      setAnalytics(res.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    }
  };

  const handlePostCreated = (newPost) => {
    if (newPost.is_pinned) {
      setPinnedPosts([newPost, ...pinnedPosts]);
    } else {
      setPosts([newPost, ...posts]);
    }
    fetchAnalytics(); // Refresh analytics
    trackAdminWorkflowCompleted("social");
    toast.success("Post published!");
  };

  const handleLike = async (postId, shouldLike) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (shouldLike) {
        await axios.post(`${API}/social/posts/${postId}/like`, {}, { headers });
      } else {
        await axios.delete(`${API}/social/posts/${postId}/like`, { headers });
      }
      
      const updatePost = (p) => 
        p.post_id === postId 
          ? { ...p, user_liked: shouldLike, like_count: p.like_count + (shouldLike ? 1 : -1) }
          : p;
      
      setPosts(posts.map(updatePost));
      setPinnedPosts(pinnedPosts.map(updatePost));
    } catch (error) {
      toast.error("Failed to update like");
    }
  };

  const handleComment = (postId) => {
    const updatePost = (p) => 
      p.post_id === postId 
        ? { ...p, comment_count: p.comment_count + 1 }
        : p;
    
    setPosts(posts.map(updatePost));
    setPinnedPosts(pinnedPosts.map(updatePost));
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/social/posts/${postId}`, { headers });
      setPosts(posts.filter(p => p.post_id !== postId));
      setPinnedPosts(pinnedPosts.filter(p => p.post_id !== postId));
      fetchAnalytics();
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handlePin = async (post) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/social/posts/${post.post_id}`,
        { is_pinned: !post.is_pinned },
        { headers }
      );
      
      fetchPosts();
      toast.success(post.is_pinned ? "Post unpinned" : "Post pinned");
    } catch (error) {
      toast.error("Failed to update pin status");
    }
  };

  const handleEdit = async (post) => {
    // TODO: Implement edit modal
    toast.info("Edit feature coming soon");
  };

  if (loading && posts.length === 0) {
    return (
      <PageLayout title="Social">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </PageLayout>
    );
  }

  if (loadError && posts.length === 0) {
    return (
      <PageLayout title="Social">
        <div className="mx-auto max-w-lg rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <h2 className="font-semibold text-red-900">Social posts could not be loaded</h2>
          <p className="mt-2 text-sm text-red-700">Your posts are still available. Check your connection and try again.</p>
          <Button className="mt-4" variant="outline" onClick={() => fetchPosts()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Retry
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout data-testid="admin-social">
      <div className="space-y-6">
        {/* Header */}
        <EditorialPageHeader
          eyebrow="Communications"
          title="Social Media"
          description="Manage your organization's social presence and engage with followers"
          headingId="social-media-heading"
          aside={<Button
            variant="outline"
            onClick={() => { fetchPosts(); fetchAnalytics(); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>}
        />

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.followers?.total || 0}</p>
                  <p className="text-sm text-gray-500">Followers</p>
                </div>
              </div>
              {analytics.followers?.new_in_period > 0 && (
                <p className="text-xs text-green-600 mt-2">
                  +{analytics.followers.new_in_period} this month
                </p>
              )}
            </div>
            
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Newspaper className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.posts?.total || 0}</p>
                  <p className="text-sm text-gray-500">Total Posts</p>
                </div>
              </div>
              {analytics.posts?.in_period > 0 && (
                <p className="text-xs text-green-600 mt-2">
                  +{analytics.posts.in_period} this month
                </p>
              )}
            </div>
            
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center">
                  <Heart className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.engagement?.total_likes || 0}</p>
                  <p className="text-sm text-gray-500">Total Likes</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Eye className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.engagement?.total_views || 0}</p>
                  <p className="text-sm text-gray-500">Total Views</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-gray-100 p-1 rounded-xl">
            <TabsTrigger value="posts" className="rounded-lg">
              <Newspaper className="w-4 h-4 mr-2" />
              Posts
            </TabsTrigger>
            <TabsTrigger value="moderation" className="rounded-lg">
              <Shield className="w-4 h-4 mr-2" />
              Moderation
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="space-y-6">
            {/* Create Post Form */}
            {user?.org_id && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Create New Post
                </h3>
                <CreatePostForm
                  orgId={user.org_id}
                  postType="org"
                  onPostCreated={handlePostCreated}
                />
              </div>
            )}

            {/* Pinned Posts */}
            {pinnedPosts.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  Pinned Posts ({pinnedPosts.length})
                </h3>
                {pinnedPosts.map((post) => (
                  <PostCard
                    key={post.post_id}
                    post={post}
                    onLike={handleLike}
                    onComment={handleComment}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                    onPin={handlePin}
                    showAnalytics={true}
                    currentUserId={user?.user_id}
                  />
                ))}
              </div>
            )}

            {/* All Posts */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                Recent Posts ({posts.length})
              </h3>
              
              {posts.length > 0 ? (
                <>
                  {posts.map((post) => (
                    <PostCard
                      key={post.post_id}
                      post={post}
                      onLike={handleLike}
                      onComment={handleComment}
                      onDelete={handleDelete}
                      onEdit={handleEdit}
                      onPin={handlePin}
                      showAnalytics={true}
                      currentUserId={user?.user_id}
                    />
                  ))}
                  
                  {hasMore && (
                    <div className="text-center pt-4">
                      <Button
                        variant="outline"
                        onClick={() => fetchPosts(true)}
                      >
                        Load More Posts
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 text-center">
                  <Newspaper className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <h4 className="text-lg font-semibold text-gray-700 mb-2">No Posts Yet</h4>
                  <p className="text-gray-500">
                    Create your first post to start engaging with your community.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="moderation" className="space-y-6">
            {user?.org_id && (
              <ModerationPanel orgId={user.org_id} />
            )}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            {analytics ? (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Engagement Summary */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Engagement Summary</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Heart className="w-5 h-5 text-rose-500" />
                        <span className="text-gray-700">Total Likes</span>
                      </div>
                      <span className="text-xl font-bold text-gray-900">
                        {analytics.engagement?.total_likes || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5 text-blue-500" />
                        <span className="text-gray-700">Total Comments</span>
                      </div>
                      <span className="text-xl font-bold text-gray-900">
                        {analytics.engagement?.total_comments || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-emerald-500" />
                        <span className="text-gray-700">Total Views</span>
                      </div>
                      <span className="text-xl font-bold text-gray-900">
                        {analytics.engagement?.total_views || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="w-5 h-5 text-indigo-500" />
                        <span className="text-gray-700">Avg. Engagement/Post</span>
                      </div>
                      <span className="text-xl font-bold text-indigo-600">
                        {analytics.engagement?.avg_engagement_per_post || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Follower Growth */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Follower Growth</h3>
                  <div className="text-center py-8">
                    <p className="text-5xl font-bold text-gray-900 mb-2">
                      {analytics.followers?.total || 0}
                    </p>
                    <p className="text-gray-500 mb-4">Total Followers</p>
                    {analytics.followers?.new_in_period > 0 && (
                      <Badge className="bg-green-100 text-green-700">
                        +{analytics.followers.new_in_period} new this month
                      </Badge>
                    )}
                  </div>
                  <div className="border-t border-gray-100 pt-4 mt-4">
                    <p className="text-sm text-gray-500 text-center">
                      Data from the last {analytics.period_days} days
                    </p>
                  </div>
                </div>

                {/* Posting Activity */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6 lg:col-span-2">
                  <h3 className="font-semibold text-gray-900 mb-4">Posting Activity</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <p className="text-3xl font-bold text-gray-900">
                        {analytics.posts?.total || 0}
                      </p>
                      <p className="text-sm text-gray-500">Total Posts</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <p className="text-3xl font-bold text-gray-900">
                        {analytics.posts?.in_period || 0}
                      </p>
                      <p className="text-sm text-gray-500">Posts This Month</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <p className="text-3xl font-bold text-gray-900">
                        {pinnedPosts.length}
                      </p>
                      <p className="text-sm text-gray-500">Pinned Posts</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <p className="text-3xl font-bold text-gray-900">
                        {Math.round((analytics.engagement?.total_likes || 0) / Math.max(analytics.posts?.total || 1, 1))}
                      </p>
                      <p className="text-sm text-gray-500">Avg. Likes/Post</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default AdminSocial;
